<script lang="ts">
	/**
	 * Groups header. New layout: renders a Toolbar with search/filter/chips and
	 * publishes the kebab (Reorder items) into `$listHeader` for the tabs action
	 * slot. Room-scoped path (type === 'room') falls through to `RoomToolbar` +
	 * the shared room breadcrumbs, matching the original component.
	 * Old layout: preserved verbatim.
	 */
	import { createEventDispatcher, onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import { ComponentType } from '@Shelly/shelly-design-system/helpers/enums';
	import {
		Toolbar,
		Breadcrumbs,
		IconButton,
		Input,
		Chip,
		Button,
		BreadcrumbsFlexWrapper,
	} from '@Shelly/shelly-design-system';
	import RoomToolbar from '../Rooms/RoomToolbar.svelte';
	import RoomBreadCrumbs from '../Rooms/RoomBreadCrumbs.svelte';
	import GroupsFilterModal from './GroupsFilterModal.svelte';
	import { edit } from 'src/stores/ui/edit_lists';
	import {
		groupMainFilter,
		resetGroupMainFilter,
		roomsGroupsFilter,
		getSelectedGroupFilterCount,
	} from 'src/stores/ui/filters';
	import { defaultSelectedRoom } from 'src/helpers/constants';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { is_mobile } from 'src/stores';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import { createListHeaderHandle, type ListHeaderConfig } from 'src/stores/list_header';
	import { capFirstChar } from 'src/helpers/utilities';

	export let type: string = 'groups';
	export let id: number | string | undefined;
	export let disableReorder = false;

	// Old-layout Toolbar wires a `submit-click` up from `AddEditButtons`; the
	// prop is intentionally unused here (parity API surface only).
	const dispatch = createEventDispatcher();
	void dispatch;

	const headerHandle = createListHeaderHandle();

	let openFilterModal = false;

	$: filterCount = getSelectedGroupFilterCount($roomsGroupsFilter);
	$: notification =
		filterCount > 0 ? ({ title: filterCount, type: ComponentType.DANGER } as const) : null;

	function resetFilters() {
		resetGroupMainFilter();
		roomsGroupsFilter.set({ rooms: [defaultSelectedRoom] });
	}

	// New layout publisher — only for the top-level "groups" list (the
	// room-scoped context is orchestrated by `RoomToolbarWrapper`).
	$: if ($showNewLayout && type === 'groups') {
		const config: ListHeaderConfig = $edit
			? {
					primaryAction: {
						label: $format('label_finish_reordering', { default: 'Finish reordering' }),
						icon: 'fas fa-xmark',
						variant: 'secondary',
						onClick: () => edit.set(false),
					},
				}
			: {
					menuItems: [
						{
							items: [
								{
									title: $format('label_reorder_items', { default: 'Reorder items' }),
									icon: 'fa-solid fa-arrows-up-down-left-right',
									disabled: disableReorder,
									onClick: () => edit.set(true),
								},
							],
						},
					],
				};
		console.log('[listHeader] publish GroupsToolbar', {
			type,
			mode: $edit ? 'primaryAction' : 'menuItems',
			disableReorder,
		});
		headerHandle.set(config);
	}

	onDestroy(() => {
		if (type === 'groups') {
			console.log('[listHeader] clear GroupsToolbar', { type });
			headerHandle.clear();
		}
	});
</script>

{#if $showNewLayout && type === 'groups'}
	{#if !$edit}
		<Toolbar
			class="mb-m"
			isSticky
			useNewLayout
			noBreadcrumbs
			hideSlotRightSide
			hideSlotBottom={!notification}
		>
			<svelte:fragment slot="leftSide">
				<div class="d-flex align-items-center justify-content-center">
					<Input
						class="width-md-l mr-sm-s text-capitalize-placeholder"
						bind:value={$groupMainFilter}
						type="text"
						label={capFirstChar($format('search', { default: 'Search' }))}
						icon="fas fa-search"
						id="groups-toolbar-search-input"
						aria-labelledby="groups-toolbar-search-input"
					/>
					<IconButton
						icon="fa-solid fa-filter"
						on:click={() => {
							openFilterModal = true;
						}}
						aria-label={$format('aria_open_filter', { default: 'Open filter' })}
						{notification}
					/>
				</div>
			</svelte:fragment>

			<svelte:fragment slot="bottom">
				{#if notification}
					<div class="d-flex align-items-center">
						{#each $roomsGroupsFilter.rooms as room}
							<Chip
								aria-label={room.title}
								showChip
								title={room.title}
								class="mr-s flex-shrink-0"
								on:close={resetFilters}
							/>
						{/each}
						<IconButton
							on:click={resetFilters}
							icon="fas fa-xmark"
							variant="secondary"
							class="width-full"
						>
							{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
						</IconButton>
					</div>
				{/if}
			</svelte:fragment>
		</Toolbar>
	{/if}
{:else}
	<Toolbar
		class="mb-m"
		isSticky
		useNewLayout={$showNewLayout}
		hideSlotLeftSide={$edit || (!$is_mobile && type !== 'room')}
		hideSlotRightSide={($is_mobile && type !== 'room') || !notification}
		hideSlotBottom={!notification || $edit || ($is_mobile && type !== 'room')}
	>
		<svelte:fragment slot="breadcrumbs">
			{#if type === 'room'}
				<RoomBreadCrumbs {id} />
			{:else if $is_mobile && type !== 'room'}
				<BreadcrumbsFlexWrapper hideSlotRight={!notification} gap="s">
					<div slot="left" class="flex-1 min-width-0 max-width-fit-content">
						<Breadcrumbs
							listClasses="width-full"
							aria-label={$format('label_groups', { default: 'Groups' })}
							links={[{ label: capFirstChar($format('tab_groups')) }]}
						/>
					</div>

					{#if !isSubAccount()}
						<Button
							slot="right"
							size="m"
							variant="secondary"
							class="align-self-center"
							icon={$edit ? '' : 'fa-solid fa-arrows-up-down-left-right'}
							disabled={disableReorder}
							on:click={() => {
								edit.set(!$edit);
							}}
						>
							{$edit
								? $format('button_finish_reordering_label', {
										default: 'Finish reordering',
									})
								: $format('reorder_items_button_label', { default: 'Reorder items' })}
						</Button>
					{/if}
				</BreadcrumbsFlexWrapper>
			{:else if !$is_mobile && type !== 'room'}
				<BreadcrumbsFlexWrapper gap="s" hideSlotRight={!notification}>
					<div
						class="d-flex align-items-center flex-1 min-width-0 max-width-fit-content"
						slot="left"
					>
						<Breadcrumbs
							class="flex-1 min-width-0 mr-s"
							listClasses="width-full"
							links={[{ label: capFirstChar($format('tab_groups')) }]}
							aria-label={$format('label_groups', { default: 'Groups' })}
						/>

						{#if !$edit}
							<div class="d-flex align-items-center justify-content-center">
								<Input
									class="width-md-l mr-sm-s text-capitalize-placeholder"
									bind:value={$groupMainFilter}
									type="text"
									label={capFirstChar($format('search', { default: 'Search' }))}
									icon="fas fa-search"
									id="groups-toolbar-search-input"
									aria-labelledby="groups-toolbar-search-input"
								/>
								<IconButton
									icon="fa-solid fa-filter"
									on:click={() => {
										openFilterModal = true;
									}}
									aria-label={$format('aria_open_filter', { default: 'Open filter' })}
									{notification}
								/>
							</div>
						{/if}
					</div>

					<Button
						slot="right"
						size="m"
						variant="secondary"
						class="align-self-center"
						icon={$edit ? '' : 'fa-solid fa-arrows-up-down-left-right'}
						disabled={disableReorder}
						on:click={() => {
							edit.set(!$edit);
						}}
					>
						{$edit
							? $format('button_finish_reordering_label', { default: 'Finish reordering' })
							: $format('reorder_items_button_label', { default: 'Reorder items' })}
					</Button>
				</BreadcrumbsFlexWrapper>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="leftSide">
			{#if type === 'room'}
				<RoomToolbar />
			{/if}

			{#if type === 'groups'}
				<div class="d-flex align-items-center justify-content-center">
					<Input
						class="width-md-l mr-sm-s text-capitalize-placeholder"
						bind:value={$groupMainFilter}
						type="text"
						label={capFirstChar($format('search', { default: 'Search' }))}
						icon="fas fa-search"
						id="groups-toolbar-search-input"
						aria-labelledby="groups-toolbar-search-input"
					/>
					<IconButton
						icon="fa-solid fa-filter"
						on:click={() => {
							openFilterModal = true;
						}}
						aria-label={$format('aria_open_filter', { default: 'Open filter' })}
						{notification}
					/>
				</div>
			{/if}
			{#if notification && $is_mobile}
				<div class="d-flex align-items-center">
					<IconButton
						on:click={resetFilters}
						icon="fas fa-xmark"
						variant="secondary"
						class="width-full"
					>
						{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
					</IconButton>
				</div>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="bottom">
			{#if notification}
				<div class="d-flex align-items-center">
					{#if !$is_mobile}
						{#each $roomsGroupsFilter.rooms as room}
							<Chip
								aria-label={room.title}
								showChip
								title={room.title}
								class="mr-s flex-shrink-0"
								on:close={resetFilters}
							/>
						{/each}
					{/if}
					<IconButton
						on:click={resetFilters}
						icon="fas fa-xmark"
						variant="secondary"
						class="width-full"
					>
						{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
					</IconButton>
				</div>
			{/if}
		</svelte:fragment>
	</Toolbar>
{/if}

{#if openFilterModal}
	<GroupsFilterModal bind:openFilterModal />
{/if}
